import React, { useEffect, useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { DateGDT } from '@/types';
import { FCupDate } from '@/types/friendCup';
import { cn } from '@/utils';

export const ArrowDateSelector = React.memo(
  ({
    matchsDaysAvailables,
    disabled,
  }: {
    matchsDaysAvailables: FCupDate[] | DateGDT[];
    disabled?: boolean;
  }) => {
    const { tourneyData, updateTourney } = useTourney();
    const location = useLocation();

    const initDayByIndex = matchsDaysAvailables.findIndex(
      (obj) => obj.orden === tourneyData.initDay
    );

    const isMinileagues =
      location.pathname.includes(ROUTES.minileagues.view) &&
      initDayByIndex !== -1;

    const initialValueState = {
      counter: isMinileagues ? initDayByIndex : matchsDaysAvailables.length - 1,
      disablingPrev: isMinileagues
        ? initDayByIndex < 1
        : matchsDaysAvailables.length <= 1,
      disablingNext: isMinileagues
        ? matchsDaysAvailables.length === initDayByIndex + 1
        : true,
    };

    const [stateDate, setStateDate] = useState(initialValueState);
    const [stateTitle, setStateTitle] = useState('');

    const handlePrevMatch = () => {
      if (stateDate.disablingPrev) return;
      setStateDate({
        ...stateDate,
        disablingNext: false,
        disablingPrev: stateDate.counter <= 1,
        counter: !(stateDate.counter === 0)
          ? stateDate.counter - 1
          : stateDate.counter,
      });
      const date = Object.values(matchsDaysAvailables)[stateDate.counter - 1];
      updateTourney({
        matchDay: date as DateGDT,
      });
      date.nombre && setStateTitle(date.nombre);
    };

    const handleNextMatch = () => {
      if (stateDate.disablingNext) return null;
      setStateDate({
        ...stateDate,
        disablingPrev: false,
        disablingNext: stateDate.counter + 2 === matchsDaysAvailables.length,
        counter: !(stateDate.counter === matchsDaysAvailables.length - 1)
          ? stateDate.counter + 1
          : stateDate.counter,
      });
      const date = Object.values(matchsDaysAvailables)[stateDate.counter + 1];
      updateTourney({
        matchDay: date as DateGDT,
      });
      date.nombre && setStateTitle(date.nombre);
    };

    // MOUNT RANKING DATA - INITIAL VALUE STATE
    useEffect(() => {
      const date = Object.values(matchsDaysAvailables)[stateDate.counter];
      if (date) {
        updateTourney({
          matchDay: date as DateGDT,
        });
      }
      setStateTitle(date?.nombre || tourneyData.matchDay?.nombre || '');
      // UNMOUNT RANKING - RESET INITIAL VALUE STATE
      return () => {
        setStateDate(initialValueState);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div
        className={cn(
          'flex items-center bg-transparent lg:bg-gdt-blue-xl3 h-8 lg:h-[3.3rem] mb-4 lg:mb-0 justify-center mt-8 lg:mt-0',
          { 'lg:bg-gdt-gray-7': disabled }
        )}
      >
        <Button
          className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
            'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
              stateDate.disablingPrev,
          })}
          disabled={disabled}
          type="button"
          variant="primary"
          onClick={handlePrevMatch}
        >
          <IoIosArrowBack className="m-auto" size={19} />
        </Button>
        <p
          className={cn(
            'text-gdt-blue text-base lg:text-lg font-bold mx-5 min-w-[76px]',
            { 'text-gdt-gray-5': disabled }
          )}
        >
          {stateTitle}
        </p>
        <Button
          className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
            'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
              stateDate.disablingNext,
          })}
          disabled={disabled}
          type="button"
          variant="primary"
          onClick={handleNextMatch}
        >
          <IoIosArrowForward className="m-auto" size={19} />
        </Button>
      </div>
    );
  }
);

ArrowDateSelector.displayName = 'ArrowDateSelector';
